import { Data } from '@generated/data'
import { usePage } from '@inertiajs/react'
import { ReactElement, useEffect } from 'react'
import { Form, Link } from '@adonisjs/inertia/react'
import { AnchoredToastProvider, ToastProvider, toastManager } from '~/components/ui/toast'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { ThemeToggle } from '~/components/custom/theme-toggle'
import { useIsMobile } from '~/hooks/use-media-query'
import MobileNavigation from './mobile-navigation'

export default function Layout({ children }: { children: ReactElement<Data.SharedProps> }) {
  const { url, flash } = usePage()

  const isMobile = useIsMobile();

  useEffect(() => {
    if (flash.error) {
      toastManager.add({
        type: 'error',
        title: 'Błąd',
        description: flash.error,
      })
    }

    if (flash.info) {
      toastManager.add({
        type: 'info',
        title: 'Informacja',
        description: flash.info,
      })
    }

    if (flash.success) {
      toastManager.add({
        type: 'success',
        title: 'Sukces',
        description: flash.success,
      })
    }
  }, [url, flash])

  return (
    <>
      {isMobile ? (
        <MobileNavigation
          user={children.props.user}
          isOrganizationMember={children.props.isOrganizationMember} 
        />
      ) : (
        <header className="bg-card border-b border-border px-5">
          <div className="container mx-auto flex h-18 py-4 items-center justify-between">
            <div>
              <Link route="home">ConTroll</Link>
            </div>
            <div>
              {children.props.user ? (
                <span>Zalogowano jako: {children.props.user.nickname}</span>
              ) : null}
            </div>
            <div className="flex items-center gap-5">
              <nav className="flex gap-5 text-muted-foreground">
                {children.props.user ? (
                  <>
                    <Link route="conventions.list">Konwenty</Link>
                    {children.props.isOrganizationMember ? (
                      <Link route="organizations.index">Organizacje</Link>
                    ) : null}
                    <Link route="profile.show">Profil</Link>
                    <Form route="session.destroy">
                      <button type="submit" className="cursor-pointer">
                        {' '}
                        Wyloguj{' '}
                      </button>
                    </Form>
                  </>
                ) : (
                  <>
                    {/* The programme is public now (ADR-0014), so a visitor
                        needs a way to reach it that is not a login screen. */}
                    <Link route="conventions.list">Konwenty</Link>
                    <Link route="new_account.create">Zarejestruj się</Link>
                    <Link route="session.create">Zaloguj się</Link>
                  </>
                )}
              </nav>
              <ThemeToggle />
            </div>
          </div>
        </header>
      )}
      <ToastProvider position="top-center">
        <AnchoredToastProvider>
          <main className="flex-1 p-4">{children}</main>
        </AnchoredToastProvider>
      </ToastProvider>
      <footer className="bg-card border-t border-border px-5">
        <div className="container mx-auto flex flex-col md:flex-row md:h-18 py-4 items-center justify-center gap-2 md:gap-4 text-muted-foreground">
          <Link route="privacy_policy" className="hover:underline">
            Polityka prywatności (ConTroll)
          </Link>
          <a
            href="/downloads/c26_polityka_prywatnosci.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            Polityka prywatności (Copernicon '26)
          </a>
          <Popover>
            <PopoverTrigger className="cursor-pointer hover:underline">Kontakt</PopoverTrigger>
            <PopoverContent align="center" className="text-sm">
              <ul className="space-y-2">
                <li>
                  Pytania związane z punktami programu:{' '}
                  <Link
                    href="mailto:program@copernicon.pl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    program@copernicon.pl
                  </Link>
                </li>
                <li>
                  Pozostałe pytania o konwent:{' '}
                  <Link
                    href="https://copernicon.pl/kontakt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    copernicon.pl/kontakt
                  </Link>
                </li>
                <li>
                  Zgłoś problem z aplikacją:{' '}
                  <Link
                    href="https://forms.gle/TaYiuAJZyxCKcrst5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Zgłoś problem
                  </Link>
                </li>
              </ul>
            </PopoverContent>
          </Popover>
        </div>
      </footer>
    </>
  )
}
